import { TestAnswer } from '../response-models/test-answer.model';
import { TestQuestion } from '../response-models/test-question.model';
import { TestResultSummary } from '../response-models/test-result-summary.model';
import { isRecord } from './is-record';
import { countOrZero, idString, nullableBoolean, nullableString } from './wire-value-utils';

/** The hub's error code when an answer arrives after the attempt's time limit (`POST /test-attempts/:id/answer`). */
export const TEST_TIME_EXPIRED = 'TEST_TIME_EXPIRED';

/** Reads the questions of `GET /safety-tests/:id/questions` (`{ test, questions }`). */
export function toTestQuestions(data: unknown): TestQuestion[] {
  const questions = isRecord(data) ? data['questions'] : undefined;
  return Array.isArray(questions) ? questions.filter(isRecord).map(toTestQuestion) : [];
}

/** Orders questions to match an attempt's `questionIds` (the hub's shuffle), dropping any not in it. */
export function orderQuestions(questions: TestQuestion[], questionIds: string[]): TestQuestion[] {
  if (questionIds.length === 0) {
    return questions;
  }
  const byId = new Map(questions.map(question => [question.id, question]));
  return questionIds.map(id => byId.get(id)).filter((question): question is TestQuestion => question !== undefined);
}

/** Reads an attempt's saved answers (missing reads as none). */
export function toTestAnswers(value: unknown): TestAnswer[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isRecord).map(answer => ({
    questionId: nullableString(answer['questionId']) ?? '',
    selectedOptionId: nullableString(answer['selectedOptionId']) ?? '',
    isCorrect: nullableBoolean(answer['isCorrect']) ?? undefined,
    answeredAt: nullableString(answer['answeredAt']) ?? undefined,
  }));
}

/** Reads the grading result of `POST /test-attempts/:id/submit`. */
export function toTestResultSummary(data: unknown): TestResultSummary {
  const source = isRecord(data) ? data : {};
  return {
    totalQuestions: countOrZero(source['totalQuestions']),
    correctAnswers: countOrZero(source['correctAnswers']),
    incorrectAnswers: countOrZero(source['incorrectAnswers']),
    score: countOrZero(source['score']),
    passed: nullableBoolean(source['passed']) ?? false,
    passingScore: countOrZero(source['passingScore']),
    timeSpent: nullableString(source['timeSpent']) ?? '',
    expiresAt: nullableString(source['expiresAt']) ?? undefined,
  };
}

function toTestQuestion(question: Record<string, unknown>): TestQuestion {
  const options = Array.isArray(question['options']) ? question['options'].filter(isRecord) : [];
  return {
    id: idString(question['id']),
    text: nullableString(question['text']) ?? '',
    type: (nullableString(question['type']) ?? 'multiple_choice') as TestQuestion['type'],
    explanation: nullableString(question['explanation']) ?? undefined,
    options: options.map(option => ({
      id: idString(option['id']),
      text: nullableString(option['text']) ?? '',
      isCorrect: nullableBoolean(option['isCorrect']) ?? undefined,
    })),
    correctOptionId: nullableString(question['correctOptionId']) ?? undefined,
  };
}
